import InfoIcon from '@mui/icons-material/Info';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { createContact, getBusinessContactsByIdentifier } from 'lib/api';
import { submitVerification } from 'lib/api/documentConfirmation';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import { TextArea } from 'lib/designSystem/TextArea';
import Uploader from 'lib/designSystem/Uploader';
import { createContactValidation, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import ConfirmInvoiceContactForm from '../ConfirmContactForm';

const availableTypes = [
  { id: 'mail', name: 'Correo' },
  { id: 'phone', name: 'Teléfono' },
  { id: 'whatsapp', name: 'Whatsapp' },
  { id: 'visit', name: 'Visita' },
  { id: 'postVerified', name: 'Post verificado' },
];

const allowedFileTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
];

const maxFileSize = 10 * 1024 * 1024; // 10MB

const ConfirmationForm = (props: any) => {
  const {
    status,
    setStatus,
    type,
    setType,
    message,
    setMessage,
    selectedDocuments,
    handleFileChange,
    uploadedFileName,
  } = props;

  const confirmTypesInDocuments = selectedDocuments.map(
    (document: any) => document.confirmType,
  );
  const availableTypeForThisDocuments = availableTypes.filter(
    (type: any) => !confirmTypesInDocuments.includes(type.name),
  );

  return (
    <div>
      <Grid container spacing={2} paddingRight={3}>
        <Grid item xs={3}>
          <Stack direction={'column'} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="status-label">{'Status'}</InputLabel>
              <Select
                labelId="status-label"
                id="Filtrar por status"
                fullWidth
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label={'Status'}
              >
                <MenuItem key={'VERIFIED'} value={'VERIFIED'}>
                  Verificada
                </MenuItem>
                <MenuItem key={'REJECTED'} value={'REJECTED'}>
                  Rechazada
                </MenuItem>
                <MenuItem key={'PENDING'} value={'PENDING'}>
                  Pendiente
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="filter-label">{'Tipo'}</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                fullWidth
                value={type}
                onChange={(e) => setType(e.target.value)}
                label={'Tipo'}
              >
                {availableTypeForThisDocuments.map((x: any, index: any) => (
                  <MenuItem key={index} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Uploader
              handleFileChange={handleFileChange}
              buttonText="Subir PDF / Imagen"
              uploadedFileName={uploadedFileName}
              accept={allowedFileTypes.join(',')}
            />
          </Stack>
        </Grid>
        <Grid item xs={9}>
          <TextArea
            minRows={8}
            maxRows={12}
            value={message}
            placeholder="Comentario"
            onChange={(e) => setMessage(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const ConfirmationModule = (props: any) => {
  const {
    documents,
    stakeholderName,
    stakeholderIdentifier,
    setUp,
    open,
    setOpen,
    pagination,
  } = props;

  const [contact, setContact] = useState<any>({
    name: '',
    email: '',
    role: '',
    phone: '',
  });
  const [newContact, setNewContact] = useState<any>(false);
  const [contacts, setContacts] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState<any>(null);

  const handleFileChange = (event: any) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile && allowedFileTypes.includes(uploadedFile.type)) {
      if (uploadedFile.size > maxFileSize) {
        showAlert(
          dispatch,
          'error',
          'El archivo excede el tamaño máximo permitido (10MB)',
        );
      } else {
        setUploadedFileName(uploadedFile.name);
        setFile(uploadedFile);
      }
    } else {
      showAlert(
        dispatch,
        'error',
        'Por favor, sube un archivo tipo .pdf, .png, .jpg o .jpeg',
      );
    }
  };

  const dispatch = useDispatch();

  const setUpContacts = async () => {
    setLoading(true);
    const contactsResponse = await getBusinessContactsByIdentifier(dispatch, {
      identifier: stakeholderIdentifier,
    });
    setContacts(contactsResponse || []);
    if (!contactsResponse.length) {
      setNewContact(true);
    }
    setLoading(false);
  };

  const validateAttributes = () => {
    if (type === 'postVerified') return true;
    if (!createContactValidation(contact, dispatch)) return false;
    return true;
  };

  const validateConfirmation = () => {
    if (type === '') {
      showAlert(dispatch, 'error', 'Debes seleccionar un tipo de confirmación');
      return false;
    }
    if (message === '') {
      showAlert(dispatch, 'error', 'Debes agregar un comentario');
      return false;
    }
    if (!status) {
      showAlert(dispatch, 'error', 'Debes seleccionar un status');
      return false;
    }
    if (!newContact && type !== 'postVerified' && !contact.id) {
      showAlert(dispatch, 'error', 'Debes seleccionar un contacto');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (stakeholderIdentifier) void setUpContacts();
  }, [stakeholderIdentifier]);

  const handleSubmit = async () => {
    setLoading(true);
    if (!validateConfirmation()) {
      setLoading(false);
      return;
    }
    try {
      const contactWithId = contact;
      if (newContact && type !== 'postVerified') {
        if (!validateAttributes()) {
          setLoading(false);
          return;
        }
        const response = await createContact(dispatch, {
          businessIdentifier: stakeholderIdentifier,
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          role: contact.role,
        });
        contactWithId.id = response.id;
        setNewContact(false);
      }
      const data = new FormData();
      data.append('type', type);
      data.append('message', message);
      data.append('contactId', contactWithId.id);
      data.append('documents', JSON.stringify(documents));
      data.append('status', status);
      if (file) {
        data.append('file', file);
      }
      await submitVerification(dispatch, {
        data,
      });
      setLoading(false);
      await setUp({ page: 0, pageSize: pagination.pageSize });
      showAlert(dispatch, 'success', 'Facturas confirmadas correctamente');
      setOpen(false);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al confirmar facturas');
    }
    setLoading(false);
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = documents.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row: any = documents.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };

  return (
    <Dialog maxWidth="xl" open={open} onClose={() => setOpen(false)}>
      {loading && <CenteredCircularProgress type="dialog" />}
      {!loading && (
        <div>
          <DialogContent>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" padding="16px 0px" fontWeight={600}>
                Verificar documentos del deudor {stakeholderName} (
                {stakeholderIdentifier})
              </Typography>
              <IconButton
                onClick={() => handleShowStakeholderDetails(documents[0].id)}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </div>
            <div style={{ padding: '10px 0px' }}>
              <EnhancedTable
                data={documents}
                dataHeaders={[
                  {
                    key: 'factoringRequestId',
                    label: 'Solicitud',
                    type: 'numeric-id',
                  },
                  { key: 'folio', label: 'Folio' },
                  {
                    key: 'businessName',
                    label: 'Cliente',
                  },
                  {
                    key: 'businessIdentifier',
                    label: 'Rut cliente',
                    handleAction: handleShowBusinessDetails,
                    icon: <InfoIcon fontSize="small" />,
                  },
                  {
                    key: 'amount',
                    label: 'Monto',
                    type: 'money',
                  },
                  {
                    key: 'factoringRequestStatus',
                    label: 'Estado Solicitud',
                    type: 'status',
                  },
                  {
                    key: 'factoringRequestCreationDate',
                    label: 'Fecha Solicitud',
                    type: 'date',
                  },
                  {
                    key: 'confirmDateSII',
                    label: 'Confirmada SII',
                    type: 'date-colored-ok',
                  },
                  {
                    key: 'confirmDate',
                    label: 'Verificación',
                    canNull: true,
                  },
                ]}
                rowsPerPageDefault={5}
                exportToFile="xlsx"
              />
            </div>
            <ConfirmationForm
              status={status}
              setStatus={setStatus}
              type={type}
              setType={setType}
              meesage={message}
              setMessage={setMessage}
              selectedDocuments={documents}
              handleFileChange={handleFileChange}
              uploadedFileName={uploadedFileName}
            />
            {type !== 'postVerified' && (
              <div>
                <div style={{ padding: '10px 0px' }}>
                  <Divider />
                </div>
                <ConfirmInvoiceContactForm
                  newContact={newContact}
                  setNewContact={setNewContact}
                  contacts={contacts}
                  setContact={setContact}
                  contact={contact}
                />
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <CustomButton color="secondary" onClick={() => setOpen(false)}>
              Salir
            </CustomButton>
            <CustomButton color="primary" onClick={() => handleSubmit()}>
              Confirmar
            </CustomButton>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

export default ConfirmationModule;
